import { z } from 'zod';
import { listRecordsQueryFieldsSchema, refineListRecordsQuery } from './records';

/**
 * The list filters a semantic search honours (#1234) — the same fields, read
 * the same way, as `GET /records`. Semantic results used to ignore them: the
 * page showed «Помилка обробки» and project X as selected, with matches from
 * outside both under it and a counter from a different query above.
 *
 * Derived from the list's schema minus paging and text search, so a filter
 * added to the list is accepted here by construction — a hand-kept field list
 * would turn every semantic search into a 400 the day one is missed.
 *
 * Built from the list's FIELDS and refined here with the list's own rule
 * (`refineListRecordsQuery`): Zod refuses `.omit` on a refined object, and a
 * rule left behind would let `crmEntityId` through without its type.
 */
export const semanticSearchFiltersSchema = listRecordsQueryFieldsSchema
  .omit({ limit: true, offset: true, search: true, searchCursor: true })
  .strict()
  .superRefine(refineListRecordsQuery);

/** Cost-bounded semantic-search request contract. */
export const semanticSearchRequestSchema = z
  .object({
    query: z.string().trim().min(1).max(2_000),
    // SearchRepository asks Vectorize for topK * 3 candidates, or 50 (its
    // ceiling with metadata) under list filters; the metadata filter adds a
    // second query of the same size, so at most 100 candidates in all.
    topK: z.number().int().min(1).max(30).default(10),
    projectId: z.string().uuid().optional(),
    minScore: z.number().min(0).max(1).default(0.3),
    filters: semanticSearchFiltersSchema.optional(),
  })
  .strict();

export type SemanticSearchRequest = z.infer<typeof semanticSearchRequestSchema>;
export type SemanticSearchFilters = z.input<typeof semanticSearchFiltersSchema>;
/** The filter names the server accepts — the wire keys the Records page may send. */
export type SemanticSearchFilterName = keyof z.infer<typeof semanticSearchFiltersSchema>;

export const semanticSearchResultSchema = z.object({
  recordId: z.string(),
  score: z.number(),
  matchedText: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()),
  record: z.record(z.string(), z.unknown()),
});

export const semanticSearchResponseSchema = z.object({
  query: z.string(),
  count: z.number().int(),
  results: z.array(semanticSearchResultSchema),
  /**
   * Sent only with list filters: they set aside some of the closest matches
   * and the page came back short, so more matches inside the filters may
   * exist past the window the index returns (#1234).
   */
  narrowedByFilters: z.boolean().optional(),
});

export type SemanticSearchResult = z.infer<typeof semanticSearchResultSchema>;
export type SemanticSearchResponse = z.infer<typeof semanticSearchResponseSchema>;

/**
 * D1 refuses a `LIKE`/`GLOB` pattern longer than 50 BYTES (measured; the
 * table and the reasoning live in `packages/database/src/repositories/search-term.ts`).
 */
export const D1_LIKE_PATTERN_MAX_BYTES = 50;

/** Pattern bytes left for the input: 50 − the `%…%` wrap (2) − a margin (2). */
export const SEARCH_TERM_MAX_BYTES = D1_LIKE_PATTERN_MAX_BYTES - 2 - 2;

const LIKE_WILDCARDS = new Set(['%', '_', '\\']);
const utf8 = new TextEncoder();

/**
 * Pattern bytes one character costs, worst case over the call sites: an
 * escaped `% _ \` becomes two bytes, and lower-casing can grow a character
 * (`İ`, 2 bytes → `i` + U+0307, 3 bytes). The ONE rule for what fits: the
 * search's truncation and every «does this fit» check count with it, so a
 * value accepted as fitting is never cut to a prefix later (#1312).
 */
export function likePatternCharCost(char: string): number {
  if (LIKE_WILDCARDS.has(char)) return 2;
  return Math.max(utf8.encode(char).length, utf8.encode(char.toLowerCase()).length);
}

/** Whether the whole input fits a search pattern, counted as the search counts. */
export function fitsSearchTerm(input: string): boolean {
  let bytes = 0;
  for (const char of input) {
    bytes += likePatternCharCost(char);
    if (bytes > SEARCH_TERM_MAX_BYTES) return false;
  }
  return true;
}
